import BigNumber from 'bignumber.js'
import erc20 from 'config/abi/erc20.json'
import masterchefABI from 'config/abi/masterchef.json'
import multicall from 'utils/multicall'
import { getAddress, getMasterChefAddress } from 'utils/addressHelpers'
import farmsConfig, { XTOON_POOL_PLANNED_ALLOC_POINT } from 'config/constants/farms'
import tokens from 'config/constants/tokens'
import { getStakerTokenPerSecond } from 'utils/apy'
// import { bowClient } from 'apollo/client'
// import { PAIR_DAY_DATA_BULK } from 'apollo/queries'

// Until the add-pool script puts a farm's pool on the MasterChef, the farm uses its planned allocation points, so it
// lists as live with an estimated APR. It can't be enabled or staked until then.
const PLANNED_TOTAL_ALLOC_POINT = farmsConfig.reduce(
  (total, farm) => total + (farm.plannedAllocPoint ?? 0),
  XTOON_POOL_PLANNED_ALLOC_POINT,
)

const fetchMasterChefTotals = async () => {
  const [poolLength, totalAllocPoint, rewardPerSecond] = await multicall(masterchefABI, [
    { address: getMasterChefAddress(), name: 'poolLength' },
    { address: getMasterChefAddress(), name: 'totalAllocPoint' },
    { address: getMasterChefAddress(), name: 'cakePerSecond' },
  ])
  return {
    poolLength: new BigNumber(poolLength.toString()).toNumber(),
    totalAllocPoint: new BigNumber(totalAllocPoint.toString()),
    rewardPerSecond: new BigNumber(rewardPerSecond.toString()),
  }
}

const fetchFarms = async () => {
  // const lpAddresses = farmsConfig.map((farm) => getAddress(farm.lpAddresses).toLowerCase())
  // const { data: results } = await bowClient.query({
  //   query: PAIR_DAY_DATA_BULK(lpAddresses),
  //   fetchPolicy: 'cache-first',
  // })
  // const { pairDayDatas } = results
  const masterChef = await fetchMasterChefTotals().catch((error) => {
    console.error('Failed fetching MasterChef totals', error)
    return null
  })
  const data = await Promise.all(
    farmsConfig.map(async (farmConfig) => {
      try {
        if (!masterChef) {
          throw new Error('MasterChef totals unavailable')
        }
        const lpAddress = getAddress(farmConfig.lpAddresses)
        const tokenAddress = getAddress(farmConfig.token.address)
        // For native quote token farms (SUPRA), use wrapped token contract for reserve/decimals reads.
        const quoteTokenAddress = farmConfig.quoteToken.address
          ? getAddress(farmConfig.quoteToken.address)
          : getAddress(tokens.wcore.address)

        const calls = [
          // Balance of token in the LP contract
          {
            address: tokenAddress,
            name: 'balanceOf',
            params: [lpAddress],
          },
          // Balance of quote token on LP contract
          {
            address: quoteTokenAddress,
            name: 'balanceOf',
            params: [lpAddress],
          },
          // Balance of LP tokens in the master chef contract
          {
            address: lpAddress,
            name: 'balanceOf',
            params: [getMasterChefAddress()],
          },
          // Total supply of LP tokens
          {
            address: lpAddress,
            name: 'totalSupply',
          },
          // Token decimals
          {
            address: tokenAddress,
            name: 'decimals',
          },
          // Quote token decimals
          {
            address: quoteTokenAddress,
            name: 'decimals',
          },
        ]

        const [tokenBalanceLP, quoteTokenBlanceLP, lpTokenBalanceMC, lpTotalSupply, tokenDecimals, quoteTokenDecimals] =
          await multicall(erc20, calls)

        // While nothing is staked (including before the pool is on the MasterChef), value the farm on the whole pair, so
        // the APR estimates what the rewards pay on the pair's liquidity instead of dividing by nothing
        const stakedLp = new BigNumber(lpTokenBalanceMC)
        const baseLp = stakedLp.isZero() ? new BigNumber(lpTotalSupply) : stakedLp

        // Ratio in % a LP tokens that are in staking, vs the total number in circulation
        const lpTokenRatio = new BigNumber(lpTotalSupply).isZero() ? 0 : baseLp.div(new BigNumber(lpTotalSupply))

        // Total value in staking in quote token value
        const lpTotalInQuoteToken = new BigNumber(quoteTokenBlanceLP)
          .div(new BigNumber(10).pow(quoteTokenDecimals))
          .times(new BigNumber(2))
          .times(lpTokenRatio)

        // Amount of token in the LP that are considered staking (i.e amount of token * lp ratio)
        const tokenAmount = new BigNumber(tokenBalanceLP).div(new BigNumber(10).pow(tokenDecimals))
        const quoteTokenAmount = new BigNumber(quoteTokenBlanceLP).div(new BigNumber(10).pow(quoteTokenDecimals))

        const isOnMasterChef = farmConfig.pid < masterChef.poolLength
        const [info] = isOnMasterChef
          ? await multicall(masterchefABI, [
              {
                address: getMasterChefAddress(),
                name: 'poolInfo',
                params: [farmConfig.pid],
              },
            ])
          : [null]
        const allocPoint = isOnMasterChef
          ? new BigNumber(info?.allocPoint?._hex ?? info?.allocPoint ?? 0)
          : new BigNumber(farmConfig.plannedAllocPoint ?? 0)
        const totalAllocPoint = isOnMasterChef ? masterChef.totalAllocPoint : new BigNumber(PLANNED_TOTAL_ALLOC_POINT)
        const poolWeight = totalAllocPoint.isZero() ? new BigNumber(0) : allocPoint.div(totalAllocPoint)
        const { rewardPerSecond } = masterChef

        // const pairData = pairDayDatas.find((pair) => pair.pairAddress.toLowerCase() === lpAddress.toLowerCase())
        const pairData = undefined
        let feeApr = 0
        if (pairData) {
          const { dailyVolumeUSD, reserveUSD } = pairData
          feeApr =
            !reserveUSD || Number(reserveUSD) === 0
              ? 0
              : (Number(dailyVolumeUSD) / Number(reserveUSD)) * 365 * 100 * 0.0005
        }

        return {
          ...farmConfig,
          tokenAmount: tokenAmount.toJSON(),
          quoteTokenAmount: quoteTokenAmount.toJSON(),
          lpTotalInQuoteToken: lpTotalInQuoteToken.toJSON(),
          tokenPriceVsQuote: tokenAmount.isZero() ? null : quoteTokenAmount.div(tokenAmount).toJSON(),
          poolWeight: poolWeight.toJSON(),
          multiplier: `${allocPoint.div(100).toString()}X`,
          lpTokenBalanceMC: baseLp.div(1e18),
          feeApr,
          // tokenPerSecond: new BigNumber(rewardPerSecond).div(1e18),
          tokenPerSecond: getStakerTokenPerSecond(rewardPerSecond.isZero() ? 200000000000000000 : rewardPerSecond),
          isOnMasterChef,
        }
      } catch (error) {
        console.error(`Failed fetching farm pid=${farmConfig.pid}`, error)
        return {
          ...farmConfig,
          tokenAmount: new BigNumber(0).toJSON(),
          quoteTokenAmount: new BigNumber(0).toJSON(),
          lpTotalInQuoteToken: new BigNumber(0).toJSON(),
          tokenPriceVsQuote: null,
          poolWeight: new BigNumber(0).toJSON(),
          multiplier: '0X',
          lpTokenBalanceMC: new BigNumber(0),
          feeApr: 0,
          tokenPerSecond: new BigNumber(0),
          isOnMasterChef: false,
        }
      }
    }),
  )
  return data
}

export default fetchFarms
